import TwoColumnLayout from "layout/TwoColumnLayout";
import React from "react";
import LinkedContacts from "company/detail/linkedContacts/App";
import UserTasksPanel from "userTask/list/app";
import NotesPanel from "note/list/app";
import DocumentFromTemplatePanel from "DocumentFromTemplate/app";
import FilesPanel from "show/files/app";
import EmailsPanel from "email/list/App";
import GeneralOptionsPanel from "components/optionspanel/GeneralOptionsPanel";
import ICalFeedList from "company/detail/icalfeed/app";
import LinkedItems from "company/detail/linkedItems/App";
import AreYouSureModal from "components/modals/AreYouSureModal";
import AdminOneClientsByCompanyPanel from "adminone/AdminOneClientsByCompanyPanel/AdminOneClientsByCompanyPanel";
import CompanyMergeModal from "company/detail/merge/app";
import { type UploadFilesConfig, uploadFilesToCoreApi } from "utilities/fileUploadUtils";
import { isPanelVisible } from "utilities/panelUtils";
import CompanyActivityPanel from "./activity/App";
import { CompanyDetailSection } from "company/CompanyDetail";
import { useLoaderData } from "react-router";

const CompanyDetailApp = () => {
  const viewModel = useLoaderData<server.viewmodel.CompanyDetailFrontModel>();

  const [displayDeleteModal, setDisplayDeleteModal] = React.useState(false);
  const [displayMergeModal, setDisplayMergeModal] = React.useState(false);
  const [displayRestoreModal, setDisplayRestoreModal] = React.useState(false);

  return <TwoColumnLayout>
    <TwoColumnLayout.LeftColumn>
      <CompanyDetailSection
        company={viewModel.company}
        venueDetails={viewModel.venueDetails}
        displayBusinessCentralNumber={viewModel.displayBusinessCentralNumber}
        displayHubSpotPanel={viewModel.displayHubSpotPanel}
      />

      {isPanelVisible(viewModel.linkedContactPanelConfig) && <LinkedContacts
        company={viewModel.company}
        panelConfig={viewModel.linkedContactPanelConfig}
      />}

      {isPanelVisible(viewModel.linkedUserTaskPanelConfig) && <UserTasksPanel
        mainArea="company"
        config={viewModel.linkedUserTaskPanelConfig}
      />}

      {isPanelVisible(viewModel.linkedNotePanelConfig) && <NotesPanel
        mainArea="company"
        config={viewModel.linkedNotePanelConfig}
      />}

      {isPanelVisible(viewModel.linkedEmailPanelConfig) && <EmailsPanel
        emailTemplateType="companyEmailTemplate"
        mainArea="company"
        config={viewModel.linkedEmailPanelConfig}
      />}

      {isPanelVisible(viewModel.generateDocumentPanelConfig) && <DocumentFromTemplatePanel
        itemId={viewModel.company.companyId}
        api={window.CoreApi.Company.ExportOptionsForDetail}
        panelTitle={viewModel.generateDocumentPanelConfig.headerLabel}
      />}

      {isPanelVisible(viewModel.linkedFilePanelConfig) && <FilesPanel
        config={viewModel.linkedFilePanelConfig}
        uploadFiles={(files: File[], uploadConfig: UploadFilesConfig) => {
          return uploadFilesToCoreApi(
            window.CoreApi.File.UploadClientFiles,
            window.CoreApi.File.UploadClientFiles.absoluteUrl(),
            {
              files,
              mainArea: "company",
              parentId: viewModel.linkedFilePanelConfig.itemId
            },
            uploadConfig
          );
        }}
        fetchFilesApi={(page, perPage) => window.CoreApi.Company.PaginatedFiles({ parentId: viewModel.linkedFilePanelConfig.itemId, page, perPage })}
      />}

      {isPanelVisible(viewModel.linkedICalendarFeedPanelConfig) && <ICalFeedList
        artistId={viewModel.company.companyId}
        config={viewModel.linkedICalendarFeedPanelConfig}
      />}

      {displayDeleteModal && <AreYouSureModal
        title={RESX.GeneralLabels.Delete}
        textContent={RESX.GeneralWarnings.DeleteSure}
        onClose={() => { setDisplayDeleteModal(false); }}
        onYes={async () => window.CoreApi.Company.Delete(viewModel.company.companyId).then(() => window.location.href = viewModel.listUrl)}
        yesButtonLabel={RESX.GeneralLabels.Delete}
        yesButtonColor="red"
      />}

      {displayRestoreModal && <AreYouSureModal
        title={RESX.Buttons.Restore}
        yesButtonLabel={RESX.Buttons.Restore}
        yesButtonColor="red"
        onClose={() => { setDisplayRestoreModal(false); }}
        onYes={async () => window.CoreApi.Company.Restore(viewModel.company.companyId).then(() => { window.location.reload(); })}
      />}

      <CompanyMergeModal
        isOpen={displayMergeModal}
        companyId={viewModel.company.companyId}
        companyName={viewModel.company.name}
        onClose={() => { setDisplayMergeModal(false); }}
      />
    </TwoColumnLayout.LeftColumn>
    <TwoColumnLayout.RightColumn>
      <GeneralOptionsPanel
        data={viewModel.optionsPanel}
        links={[
          { text: RESX.Buttons.ListView, visible: true, href: viewModel.listUrl },
          { text: RESX.OptionsPanel.Merge, visible: viewModel.company.permissions.canMerge, onClick: () => { setDisplayMergeModal(true); } },
          { text: RESX.Buttons.btnDelete, visible: viewModel.company.permissions.canDelete, onClick: () => { setDisplayDeleteModal(true); } },
          { text: RESX.Buttons.Restore, visible: viewModel.company.permissions.canRestore, onClick: () => { setDisplayRestoreModal(true); } }
        ]}
        shortcutOrder={["contact", "task", "note", "email", "file", "icalFeed"]}
      />

      {isPanelVisible(viewModel.relatedAdminOneClientPanelConfig) && <AdminOneClientsByCompanyPanel
        config={viewModel.relatedAdminOneClientPanelConfig}
      />}

      {isPanelVisible(viewModel.linkedItemCountPanelConfig) && <LinkedItems
        config={viewModel.linkedItemCountPanelConfig}
        linkedItems={viewModel.linkedItemCounts}
      />}

      {isPanelVisible(viewModel.activityPanelConfig) && <CompanyActivityPanel
        companyId={viewModel.company.companyId}
        config={viewModel.activityPanelConfig}
      />}
    </TwoColumnLayout.RightColumn>
  </TwoColumnLayout>;
};

export default CompanyDetailApp;